import {
  faExternalLinkAlt,
  faEye,
  faSort,
  faSortDown,
  faSortUp,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { Fragment, useMemo, useState } from 'react';
import { Button, Table } from 'react-bootstrap';
import { useGlobalFilter, useSortBy, useTable } from 'react-table';
import ConfirmationModal from '../../helpers/ConfirmationModal';
import GlobalTableFilter from '../../helpers/GlobalTableFilter';

const TrappingRunsTable = (props) => {
  const [showDeletedTrappingRuns, setShowDeletedTrappingRuns] = useState(false);

  const hideSearch = props.customOptions?.hideSearch;
  const hideShowDeleted = props.customOptions?.hideShowDeleted;
  const hideActions = props.customOptions?.hideActions;
  const headerNames = props.customOptions?.headerNames;

  const data = useMemo(
    () =>
      props.trappingRuns.filter(
        (trappingRun) =>
          showDeletedTrappingRuns || !trappingRun.property_group.deleted_at
      ),
    [props.trappingRuns, showDeletedTrappingRuns]
  );

  const columns = useMemo(
    () => [
      {
        Header: headerNames?.name ? headerNames?.name : 'Name',
        accessor: 'property_group.name'
      },
      {
        Header: headerNames?.description
          ? headerNames?.description
          : 'Description',
        accessor: 'property_group.description'
      },
      {
        Header: headerNames?.passcode ? headerNames?.passcode : 'Passcode',
        accessor: 'passcode'
      },
      {
        id: 'actions',
        sortable: false,
        filterable: false,
        Header: (tableInstance) => {
          if (hideShowDeleted) return null;
          return (
            <div className='text-right'>
              <Button
                type='button'
                variant='link'
                className={
                  tableInstance.state.showDeletedTrappingRuns
                    ? 'text-primary'
                    : 'text-dark'
                }
                onClick={() => {
                  setShowDeletedTrappingRuns(
                    !tableInstance.state.showDeletedTrappingRuns
                  );
                }}
              >
                <FontAwesomeIcon icon={faEye} />
              </Button>
            </div>
          );
        },
        Cell: ({ row }) => {
          return (
            <div className='text-right'>
              {/* Select property group */}
              <Button
                type='button'
                variant='link'
                className='text-primary'
                onClick={() =>
                  props.changeSelectedTrappingRunId(row.original.id)
                }
              >
                <FontAwesomeIcon icon={faExternalLinkAlt} />
              </Button>

              {/* Delete property group */}
              {row.original.property_group.deleted_at === null &&
              row.original.property_group.userProperty.can_edit ? (
                <ConfirmationModal
                  title='Confirm Delete'
                  bodyText='Are you sure you want to delete this trapping run?'
                  confirmAction={() => {
                    return props.deleteTrappingRun(row.original);
                  }}
                  showButton={{
                    children: <FontAwesomeIcon icon={faTrash} />,
                    props: {
                      className: 'text-danger',
                      variant: 'link'
                    }
                  }}
                  confirmButton={{
                    props: { variant: 'danger' }
                  }}
                />
              ) : null}
            </div>
          );
        }
      }
    ],
    [props.deleteTrappingRun]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state,
    preGlobalFilteredRows,
    setGlobalFilter
  } = useTable(
    {
      columns,
      data,
      useControlledState: (state) => {
        return React.useMemo(
          () => ({
            ...state,
            showDeletedTrappingRuns: showDeletedTrappingRuns
          }),
          [state, showDeletedTrappingRuns]
        );
      }
    },
    useGlobalFilter,
    useSortBy
  );

  return (
    <Fragment>
      {!hideSearch && (
        <GlobalTableFilter
          preGlobalFilteredRows={preGlobalFilteredRows}
          globalFilter={state.globalFilter}
          setGlobalFilter={setGlobalFilter}
          style={{
            marginBottom: '0.5rem',
            maxWidth: 500
          }}
        />
      )}
      <Table responsive {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => {
                if (hideActions && column?.id === 'actions') return null;
                return (
                  <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                    {column.render('Header')}
                    {column.canSort ? (
                      <span className='px-2'>
                        {column.isSorted ? (
                          column.isSortedDesc ? (
                            <FontAwesomeIcon icon={faSortDown} />
                          ) : (
                            <FontAwesomeIcon icon={faSortUp} />
                          )
                        ) : (
                          <FontAwesomeIcon icon={faSort} />
                        )}
                      </span>
                    ) : null}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => {
                  if (hideActions && cell.column?.id === 'actions') return null;
                  return (
                    <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </Table>
    </Fragment>
  );
};

TrappingRunsTable.propTypes = {
  trappingRuns: PropTypes.array.isRequired,
  changeSelectedTrappingRunId: PropTypes.func.isRequired,
  deleteTrappingRun: PropTypes.func.isRequired
};

export default observer(TrappingRunsTable);
